'use client';

import React from 'react';

interface CookieDecorativeCardProps {
  children: React.ReactNode;
  className?: string;
}

/**
 * Shared decorative shell used by the cookie banner and the preferences modal.
 * Recreates the SVG design: white card + soft shadow + blurred blue/green
 * gradient blobs + a glassmorphism parallelogram behind the content.
 */
export default function CookieDecorativeCard({ children, className = '' }: CookieDecorativeCardProps) {
  return (
    <div
      className={`relative overflow-hidden rounded-none bg-white border border-[#F1F2F9] shadow-[0_4px_24px_rgba(111,108,143,0.16)] ${className}`}
    >
      {/* Decorative background layers */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
        {/* blue blob - top right */}
        <div
          className="absolute -top-40 -right-28 h-[420px] w-[420px] rounded-full opacity-25 blur-[80px]"
          style={{ background: 'linear-gradient(135deg,#78C4FB,#0086E6)' }}
        />
        {/* blue blob - bottom right */}
        <div
          className="absolute -bottom-48 right-8 h-[420px] w-[420px] rounded-full opacity-[0.18] blur-[90px]"
          style={{ background: 'linear-gradient(135deg,#78C4FB,#0086E6)' }}
        />
        {/* green blob - left */}
        <div
          className="absolute top-6 -left-32 h-[360px] w-[300px] rounded-full opacity-25 blur-[70px]"
          style={{ background: 'linear-gradient(135deg,#BEEC53,#8ABC00)' }}
        />
        {/* glassmorphism parallelogram (matches the slanted shape in the SVG) */}
        <div
          className="absolute inset-0 backdrop-blur-[2.5px]"
          style={{
            clipPath: 'polygon(48.7% 11.9%, 80.7% 36.1%, 62.5% 55%, 19.2% 22.4%)',
            background:
              'linear-gradient(150deg, rgba(255,255,255,0.45) 0%, rgba(255,255,255,0.02) 85%)',
            boxShadow: 'inset 0 0 0 0.7px rgba(255,255,255,0.6)',
          }}
        />
      </div>

      {/* Content */}
      <div className="relative">{children}</div>
    </div>
  );
}

/** Green gradient tile with the cookie icon (top-left of each card). */
export function CookieIconTile() {
  return (
    <div
      className="flex h-[62px] w-[62px] flex-shrink-0 items-center justify-center rounded-lg"
      style={{
        background: 'linear-gradient(180deg,#F3F8E6 0%,#DBEAB0 100%)',
        border: '1px solid #DBEAB0',
      }}
    >
      <svg width="30" height="30" viewBox="0 0 34 34" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M27.3 10.5c-.66.42-1.46.7-2.33.7a4.36 4.36 0 0 1-4.34-4.34c0-.94.31-1.8.8-2.5a4.34 4.34 0 0 1-2.99-4.13c0-.8.24-1.53.62-2.15-1.04 0-2.12.24-3.16.24-1.67 0-3.4-.59-4.9-.1-1.56.48-2.6 2.01-3.89 2.95-1.32.94-3.06 1.5-4 2.78-.93 1.29-.9 3.13-1.42 4.69C1.11 10.11 0 11.57 0 13.23c0 1.67 1.11 3.13 1.6 4.62.48 1.57.48 3.37 1.42 4.69.94 1.32 2.71 1.84 4 2.78 1.28.94 2.32 2.43 3.89 2.96 1.49.48 3.23-.11 4.9-.11 1.66 0 3.4.59 4.89.11 1.57-.49 2.61-2.02 3.9-2.96 1.32-.93 3.05-1.49 3.99-2.78.94-1.28.9-3.12 1.42-4.69.49-1.49 1.6-2.95 1.6-4.58h-.03c-1.95 0-3.65-1.22-4.35-2.92"
          transform="translate(0.5 3)"
          fill="#8ABC00"
        />
        <circle cx="26.5" cy="8.4" r="1.7" fill="#8ABC00" />
        <circle cx="23.4" cy="2.1" r="1.3" fill="#8ABC00" />
        <circle cx="10" cy="16" r="1.6" fill="#fff" />
        <circle cx="15.5" cy="22" r="1.6" fill="#fff" />
        <circle cx="21" cy="17.5" r="1.4" fill="#fff" />
      </svg>
    </div>
  );
}

/** Circular close (✕) button used at the top-right of each card. */
export function CookieCloseButton({ onClick }: { onClick?: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Close"
      className="flex h-[30px] w-[30px] flex-shrink-0 items-center justify-center rounded-full border border-[#292D32] text-[#292D32] transition-colors hover:bg-[#292D32]/5"
    >
      <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
        <path
          d="M13 7l-6 6M7 7l6 6"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </button>
  );
}
